import React from "react";
import classNames from "classnames";
import Link from "next/link";
import pluralize from "pluralize";
import { useSelector } from "react-redux";

import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import { getCurrentDispensary } from "redux/selectors/dispensary";
import { dispensaryHref } from "utils/dispensaryUrls";
import isMobileDevice from "utils/isMobileDevice";
import { Action, Category, Label } from "utils/trackEvent";

import StarRating from "components/botanic/StarRating";

import RatingCircle from "./RatingCircle";

const ReviewsHeader: React.FC = () => {
  const { publishEvent } = useEventTracker();
  const isMobile = isMobileDevice();
  const countryCode = useDomainCountryCode();

  const {
    name,
    slug,
    atmosphereRating,
    qualityRating,
    serviceRating,
    rating,
    id,
    retailType,
    reviewCount,
  } = useSelector(getCurrentDispensary);

  const publishWriteReviewEvent = () => {
    publishEvent({
      action: Action.click,
      category: Category.dispensaryMenu,
      dispensaryId: id,
      label: Label.writeReview,
    });
  };

  const writeReviewHref = dispensaryHref({
    additionalPath: "/write-review",
    countryCode,
    retailType,
    slug,
  });

  return (
    <div>
      <div className="section__header">
        <h3 className="heading--m">
          {`${reviewCount || 0} ${pluralize("Review", reviewCount)} of ${name}`}
        </h3>
      </div>
      {reviewCount > 0 ? (
        <StarRating
          rating={rating || 0}
          starWidth={18}
          className="mb-xl text-sm"
          ratingCount={reviewCount}
        />
      ) : (
        <></>
      )}
      <div className="flex mb-xl">
        <RatingCircle
          rating={qualityRating}
          label="Quality"
          className="mr-xxl"
        />
        <RatingCircle
          rating={serviceRating}
          label="Service"
          className="mr-xxl"
        />
        <RatingCircle rating={atmosphereRating} label="Atmosphere" />
      </div>
      <div data-testid="write-review">
        <Link
          prefetch={false}
          href={writeReviewHref}
          className={classNames("button button--secondary", {
            "button--full text-sm": isMobile,
            "button--small text-xs": !isMobile,
          })}
          data-testid="write-review-button"
          onClick={publishWriteReviewEvent}
        >
          write a review
        </Link>
      </div>
    </div>
  );
};

export default ReviewsHeader;
